import React, { memo, useState, useMemo, useCallback } from 'react';
import { ScrollView, RefreshControl, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { MarketplaceListing, RecentlyViewedItem } from '@/types/residents/marketplaceListing';
import { PageHeader } from '../PageHeader';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { MarketplaceBanner } from './MarketplaceBanner';
import { MarketplaceSearchBar } from './MarketplaceSearchBar';
import { MarketplaceCategoryFilter, MarketplaceCategory } from './MarketplaceCategoryFilter';
import { MarketplaceRecentlyViewed } from './MarketplaceRecentlyViewed';
import { MarketplaceListingsGrid } from './MarketplaceListingsGrid';
import { filterMarketplaceListings } from './marketplaceUtils';

interface MarketplaceContentProps {
  listings: MarketplaceListing[];
  recentlyViewed: RecentlyViewedItem[];
  isLoading: boolean;
  refreshing: boolean;
  propertyName?: string;
  onRefresh: () => void;
  onPostItem: () => void;
  onMyProfile: () => void;
  onListingPress: (listing: MarketplaceListing) => void;
  onRecentlyViewedPress: (item: RecentlyViewedItem) => void;
  onClearRecentlyViewed: () => void;
  onViewAllListings?: () => void;
  isDesktop?: boolean;
  itemStyle?: StyleProp<ViewStyle>;
}

export const MarketplaceContent: React.FC<MarketplaceContentProps> = memo(({
  listings,
  recentlyViewed,
  isLoading,
  refreshing,
  propertyName,
  onRefresh,
  onPostItem,
  onMyProfile,
  onListingPress,
  onRecentlyViewedPress,
  onClearRecentlyViewed,
  onViewAllListings,
  isDesktop = false,
  itemStyle,
}) => {
  const insets = useSafeAreaInsets();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<MarketplaceCategory>('All');

  const filteredListings = useMemo(
    () => filterMarketplaceListings(listings, selectedCategory, searchQuery),
    [listings, selectedCategory, searchQuery],
  );

  const subtitle = propertyName
    ? `${propertyName} · ${listings.length} listing${listings.length !== 1 ? 's' : ''}`
    : `${listings.length} listing${listings.length !== 1 ? 's' : ''}`;

  const { colors } = useThemeColors();
  const handleClearSearch = useCallback(() => setSearchQuery(''), []);

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        isDesktop && styles.scrollContentDesktop,
        !isDesktop && { paddingBottom: 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor={colors.text.primary}
        />
      }
    >
      {!isDesktop && <ScreenBackLink style={styles.backLink} />}
      <PageHeader title="Marketplace" subtitle={subtitle} />

      <MarketplaceBanner onPostItem={onPostItem} onMyProfile={onMyProfile} />

      <MarketplaceSearchBar
        value={searchQuery}
        onChangeText={setSearchQuery}
        onClear={handleClearSearch}
      />

      <MarketplaceCategoryFilter
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      {recentlyViewed.length > 0 && (
        <MarketplaceRecentlyViewed
          items={recentlyViewed}
          onItemPress={onRecentlyViewedPress}
          onClear={onClearRecentlyViewed}
        />
      )}

      <MarketplaceListingsGrid
        listings={filteredListings}
        onListingPress={onListingPress}
        onViewAll={onViewAllListings}
        isLoading={isLoading}
        itemStyle={itemStyle}
      />
    </ScrollView>
  );
});

MarketplaceContent.displayName = 'MarketplaceContent';

const HP = REDESIGN_SPACING.horizontalPadding;

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
    gap: 12,
  },
  scrollContentDesktop: {
    paddingBottom: 60,
  },
  backLink: {
    marginBottom: -4,
  },
});
